import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabase';

interface DraftSheetPageProps {
  leagueId: string;
  teamId: string;
  onBack?: () => void;
  onNavigate?: (route: string) => void;
}

interface DraftPlayer {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  overall: number;
  is_hitter: boolean;
  age: number;
  power: number;
  contact: number;
  speed: number;
  discipline: number;
  fielding: number;
  arm: number;
  range_attr: number;
  glove_attr: number;
  velocity: number;
  control: number;
  movement: number;
  deception: number;
  endurance: number;
  salary_requirement: number;
}

// Position display order (BaseHit-style grouping)
const POSITION_GROUPS = ['P', 'C', '1B', '2B', '3B', 'SS', 'OF', 'UTIL'] as const;
type PositionGroup = typeof POSITION_GROUPS[number];

function displayPos(pos: string): string {
  const u = pos.toUpperCase();
  if (['P', 'SP', 'RP', 'CL', 'LR'].includes(u)) return 'P';
  if (['LF', 'CF', 'RF', 'OF'].includes(u)) return 'OF';
  if (['DH', 'UTIL', 'IF'].includes(u)) return 'UTIL';
  return u;
}

function getPosGroup(pos: string): PositionGroup {
  const u = pos.toUpperCase();
  if (u === 'P' || u.startsWith('SP') || u.startsWith('RP') || u === 'CL' || u === 'LR') return 'P';
  if (['C'].includes(u)) return 'C';
  if (['1B'].includes(u)) return '1B';
  if (['2B'].includes(u)) return '2B';
  if (['3B'].includes(u)) return '3B';
  if (['SS'].includes(u)) return 'SS';
  if (['OF', 'LF', 'CF', 'RF'].includes(u)) return 'OF';
  if (['DH', 'UTIL', 'IF'].includes(u)) return 'UTIL';
  return 'P'; // fallback
}

function getHitterArchetype(p: DraftPlayer): string | null {
  if (p.power >= 75) return 'Power';
  if (p.contact >= 70) return 'Contact';
  if (p.speed >= 75 && (p.range_attr >= 60 || p.glove_attr >= 60)) return 'Speed';
  if (p.discipline >= 70) return 'Eyes';
  if (p.power >= 60 && p.contact >= 60) return 'Balanced';
  return null;
}

function getPitcherArchetype(p: DraftPlayer): string | null {
  if (p.velocity >= 75) return 'Flame';
  if (p.control >= 70 && p.movement >= 65) return 'Control';
  if (p.deception >= 70) return 'Decep.';
  if (p.velocity >= 65 && p.endurance >= 65) return 'Power';
  return null;
}

function ovrColor(ovr: number) {
  if (ovr >= 80) return '#f59e0b';
  if (ovr >= 70) return '#22c55e';
  if (ovr >= 60) return '#3b82f6';
  return '#9ca3af';
}

function statColor(v: number) {
  if (v >= 75) return '#22c55e';
  if (v >= 60) return '#60a5fa';
  if (v >= 50) return '#fbbf24';
  return '#9ca3af';
}

export default function DraftSheetPage({ leagueId, teamId, onBack, onNavigate }: DraftSheetPageProps) {
  const [queue, setQueue] = useState<DraftPlayer[]>([]); // priority-ordered
  const [isLocked, setIsLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(900);
  const [totalTime, setTotalTime] = useState(900);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const editRef = useRef<HTMLInputElement>(null);

  // All pool players (unranked), grouped by position
  const [poolByPos, setPoolByPos] = useState<Record<PositionGroup, DraftPlayer[]>>(
    Object.fromEntries(POSITION_GROUPS.map(g => [g, []])) as unknown as Record<PositionGroup, DraftPlayer[]>
  );

  // Count of ranked players per position


  // All ranked players grouped by position for display
  // We compute this from queue
  const getQueueByPos = useCallback(() => {
    const byPos: Record<PositionGroup, { player: DraftPlayer; absIdx: number }[]> =
      Object.fromEntries(POSITION_GROUPS.map(g => [g, []])) as any;
    queue.forEach((p, idx) => {
      const g = getPosGroup(p.position);
      byPos[g].push({ player: p, absIdx: idx });
    });
    return byPos;
  }, [queue]);

  useEffect(() => {
    loadPlayers();
    loadTimer();
  }, [leagueId]);

  useEffect(() => {
    if (editingIdx !== null && editRef.current) {
      editRef.current.focus();
      editRef.current.select();
    }
  }, [editingIdx]);

  const loadPlayers = async () => {
    const { data } = await supabase
      .from('players')
      .select('id,first_name,last_name,position,overall,is_hitter,age,power,contact,speed,discipline,fielding,arm,range_attr,glove_attr,velocity,control,movement,deception,endurance,salary_requirement')
      .eq('league_id', leagueId)
      .is('team_id', null)
      .eq('is_scrub', false)
      .order('overall', { ascending: false });

    if (!data) return;
    const players = data as DraftPlayer[];

    // Load existing priority sheet
    const { data: sheet } = await supabase
      .from('draft_priority_sheets')
      .select('player_id, priority_rank')
      .eq('team_id', teamId)
      .eq('league_id', leagueId)
      .order('priority_rank', { ascending: true });

    if (sheet && sheet.length > 0) {
      const rankedIds = new Set(sheet.map((r: any) => r.player_id));
      const orderedIds = sheet.map((r: any) => r.player_id);
      const qPlayers = orderedIds
        .map((id: string) => players.find(p => p.id === id))
        .filter(Boolean) as DraftPlayer[];
      setQueue(qPlayers);
      distributePool(players.filter(p => !rankedIds.has(p.id)));
    } else {
      setQueue([]);
      distributePool(players);
    }
  };

  const distributePool = (pool: DraftPlayer[]) => {
    const byPos = Object.fromEntries(POSITION_GROUPS.map(g => [g, []])) as unknown as Record<PositionGroup, DraftPlayer[]>;
    pool.forEach(p => {
      byPos[getPosGroup(p.position)].push(p);
    });
    setPoolByPos(byPos);
  };

  const loadTimer = async () => {
    const { data: league } = await supabase
      .from('leagues')
      .select('setup_window_end, status')
      .eq('id', leagueId)
      .single();
    if (league?.status !== 'drafting') { setIsLocked(true); setTimeRemaining(0); return; }
    if (league?.setup_window_end) {
      const endTime = new Date(league.setup_window_end).getTime();
      const rem = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
      setTimeRemaining(rem);
      setTotalTime(rem > 0 ? rem : 900);
      setIsLocked(rem <= 0);
    }
  };

  // Countdown
  useEffect(() => {
    if (isLocked || timeRemaining <= 0) return;
    const t = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) { setIsLocked(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [isLocked]);

  // === CASCADE LOGIC ===
  const addToQueue = useCallback((player: DraftPlayer) => {
    if (isLocked) return;
    // Add to end of queue
    setQueue(q => [...q, player]);
    // Remove from pool
    setPoolByPos(prev => {
      const g = getPosGroup(player.position);
      return { ...prev, [g]: prev[g].filter(p => p.id !== player.id) };
    });
    setDirty(true);
  }, [isLocked]);

  const removeFromQueue = useCallback((player: DraftPlayer) => {
    if (isLocked) return;
    setQueue(q => q.filter(p => p.id !== player.id));
    // Re-add to pool
    setPoolByPos(prev => {
      const g = getPosGroup(player.position);
      const updated = [...prev[g], player].sort((a, b) => b.overall - a.overall);
      return { ...prev, [g]: updated };
    });
    setDirty(true);
  }, [isLocked]);

  // Cascade priority reorder — exact BaseHit behavior
  const cascadePriority = useCallback((currentIdx: number, newRank: number) => {
    if (isLocked) return;
    // newRank is 1-based. Clamp to valid range.
    const qLen = queue.length;
    const clampedRank = Math.max(1, Math.min(newRank, qLen));
    if (clampedRank === currentIdx + 1) return; // no change

    setQueue(prev => {
      const next = [...prev];
      const [moved] = next.splice(currentIdx, 1);
      const targetIdx = clampedRank - 1;
      next.splice(targetIdx, 0, moved);
      return next;
    });
    setDirty(true);
    setEditingIdx(null);
  }, [isLocked, queue.length]);

  // === SAVE ===
  const saveSheet = async () => {
    setSaving(true);
    await supabase.from('draft_priority_sheets').delete().eq('team_id', teamId).eq('league_id', leagueId);
    if (queue.length > 0) {
      const rows = queue.map((p, i) => ({
        team_id: teamId,
        league_id: leagueId,
        player_id: p.id,
        priority_rank: i + 1,
        updated_at: new Date().toISOString(),
      }));
      await supabase.from('draft_priority_sheets').insert(rows);
    }
    setDirty(false);
    setSaving(false);
  };

  const formatTime = (s: number) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
  const timerPct = totalTime > 0 ? (timeRemaining / totalTime) * 100 : 0;
  const timerColor = timerPct > 50 ? '#22c55e' : timerPct > 20 ? '#f59e0b' : '#ef4444';

  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === 'Enter') {
      const val = parseInt(e.currentTarget.textContent || '', 10);
      if (!isNaN(val) && val >= 1) {
        cascadePriority(idx, val);
      } else {
        setEditingIdx(null);
      }
    }
    if (e.key === 'Escape') {
      setEditingIdx(null);
    }
  };

  // Render player row (used both in queue and pool sections)
  const renderPlayerRow = (
    player: DraftPlayer,
    absIdx: number, // absolute index in the queue, or -1 if in pool
    showRank: boolean
  ) => {
    const arch = player.is_hitter ? getHitterArchetype(player) : getPitcherArchetype(player);
    const rank = absIdx + 1;

    return (
      <tr
        key={player.id}
        style={{
          borderBottom: '1px solid #1a1f2f',
          background: absIdx >= 0 ? (absIdx % 2 === 0 ? 'transparent' : '#070b14') : 'transparent',
        }}
      >
        {/* Priority number column — editable if ranked */}
        <td style={{ padding: '6px 8px', textAlign: 'center', width: 52, verticalAlign: 'middle' }}>
          {showRank ? (
            editingIdx === absIdx ? (
              <input
                ref={editRef}
                type="text"
                defaultValue={rank}
                onBlur={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1) cascadePriority(absIdx, val);
                  else setEditingIdx(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const val = parseInt((e.target as HTMLInputElement).value, 10);
                    if (!isNaN(val) && val >= 1) cascadePriority(absIdx, val);
                    else setEditingIdx(null);
                  }
                  if (e.key === 'Escape') setEditingIdx(null);
                }}
                style={{
                  width: 38,
                  padding: '2px 4px',
                  borderRadius: 4,
                  border: '2px solid #3b82f6',
                  background: '#0d1117',
                  color: '#60a5fa',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  textAlign: 'center',
                  outline: 'none',
                }}
              />
            ) : (
              <span
                onClick={() => !isLocked && setEditingIdx(absIdx)}
                style={{
                  display: 'inline-block',
                  minWidth: 32,
                  padding: '3px 6px',
                  borderRadius: 4,
                  background: isLocked ? 'transparent' : '#0d1117',
                  border: isLocked ? '1px solid #1e293b' : '1px solid #2a3040',
                  color: '#60a5fa',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  cursor: isLocked ? 'default' : 'pointer',
                  textAlign: 'center',
                  fontFamily: 'monospace',
                }}
              >
                {rank}
              </span>
            )
          ) : (
            <span
              onClick={() => !isLocked && addToQueue(player)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: `2px solid ${isLocked ? '#1e293b' : '#3b82f6'}`,
                color: isLocked ? '#1e293b' : '#60a5fa',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: isLocked ? 'default' : 'pointer',
                opacity: isLocked ? 0.3 : 0.7,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => { if (!isLocked) e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { if (!isLocked) e.currentTarget.style.opacity = '0.7'; }}
            >
              +
            </span>
          )}
        </td>

        {/* Overall */}
        <td style={{ padding: '4px 6px', textAlign: 'center', width: 36, verticalAlign: 'middle' }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            borderRadius: 6,
            background: ovrColor(player.overall) + '18',
            border: `1px solid ${ovrColor(player.overall)}33`,
            color: ovrColor(player.overall),
            fontSize: '0.8rem',
            fontWeight: 800,
          }}>
            {player.overall}
          </span>
        </td>

        {/* Name + Position */}
        <td style={{ padding: '4px 8px', verticalAlign: 'middle' }}>
          <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
            {player.first_name} {player.last_name}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 1 }}>
            <span style={{
              fontSize: '0.65rem',
              fontWeight: 700,
              color: player.is_hitter ? '#93c5fd' : '#86efac',
              background: player.is_hitter ? '#1e3a5f' : '#14532d',
              padding: '1px 5px',
              borderRadius: 3,
            }}>
              {displayPos(player.position)}
            </span>
            {arch && (
              <span style={{ fontSize: '0.58rem', fontWeight: 600, color: '#6b7280' }}>
                {arch}
              </span>
            )}
          </div>
        </td>

        {/* Key attribute bars */}
        <td style={{ padding: '4px 6px', verticalAlign: 'middle', width: 160 }}>
          {player.is_hitter ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MiniStat label="POW" value={player.power} />
              <MiniStat label="CON" value={player.contact} />
              <MiniStat label="FLD" value={player.fielding} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <MiniStat label="VEL" value={player.velocity} />
              <MiniStat label="CTL" value={player.control} />
              <MiniStat label="MV" value={player.movement} />
            </div>
          )}
        </td>

        {/* Age + salary */}
        <td style={{ padding: '4px 6px', verticalAlign: 'middle', fontSize: '0.7rem', color: '#6b7280', textAlign: 'center', width: 60 }}>
          <div>{player.age}yr</div>
          <div style={{ fontSize: '0.65rem' }}>${player.salary_requirement}k</div>
        </td>

        {/* Remove button (only for ranked) */}
        <td style={{ padding: '4px 4px', textAlign: 'center', width: 30, verticalAlign: 'middle' }}>
          {showRank && !isLocked && (
            <span
              onClick={() => removeFromQueue(player)}
              style={{
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: '0.85rem',
                opacity: 0.5,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
              title="Remove from priority list"
            >
              ✕
            </span>
          )}
        </td>
      </tr>
    );
  };

  // Render a position group section
  const renderPosGroup = (group: PositionGroup) => {
    const poolPlayers = poolByPos[group] || [];
    const queuePlayers = getQueueByPos()[group] || [];
    const totalRanked = queuePlayers.length;
    const totalPool = poolPlayers.length;
    const total = totalRanked + totalPool;

    if (total === 0) return null;

    return (
      <div key={group} style={{ marginBottom: 4 }}>
        {/* Position header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          background: '#0a0e17',
          borderBottom: '2px solid #1e293b',
          borderTop: group !== POSITION_GROUPS[0] ? '3px solid #1a1f30' : 'none',
        }}>
          <span style={{
            fontSize: '0.85rem',
            fontWeight: 800,
            color: '#e2e8f0',
            letterSpacing: 1,
          }}>
            {group === 'OF' ? 'Outfielders' : group === 'P' ? 'Pitchers' : group === 'UTIL' ? 'Utility/DH' : `${group}s`}
          </span>
          <span style={{ fontSize: '0.68rem', color: '#6b7280' }}>
            {total} available
          </span>
          {totalRanked > 0 && (
            <span style={{
              fontSize: '0.65rem',
              color: '#60a5fa',
              background: '#1e40af25',
              padding: '1px 6px',
              borderRadius: 8,
            }}>
              {totalRanked} ranked
            </span>
          )}
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <colgroup>
            <col style={{ width: 52 }} />
            <col style={{ width: 36 }} />
            <col />
            <col style={{ width: 160 }} />
            <col style={{ width: 60 }} />
            <col style={{ width: 30 }} />
          </colgroup>
          <thead>
            <tr style={{ borderBottom: '1px solid #1a1f2f' }}>
              <th style={{ padding: '4px 8px', textAlign: 'center', fontSize: '0.62rem', color: '#4b5563', fontWeight: 600 }}>#</th>
              <th style={{ padding: '4px 6px', textAlign: 'center', fontSize: '0.62rem', color: '#4b5563', fontWeight: 600 }}>OVR</th>
              <th style={{ padding: '4px 8px', textAlign: 'left', fontSize: '0.62rem', color: '#4b5563', fontWeight: 600 }}>Player</th>
              <th style={{ padding: '4px 6px', textAlign: 'left', fontSize: '0.62rem', color: '#4b5563', fontWeight: 600 }}>Attributes</th>
              <th style={{ padding: '4px 6px', textAlign: 'center', fontSize: '0.62rem', color: '#4b5563', fontWeight: 600 }}>Age</th>
              <th style={{ padding: '4px 4px', width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {/* Ranked players first (in order) */}
            {queuePlayers.map(({ player, absIdx }) => (
              renderPlayerRow(player, absIdx, true)
            ))}
            {/* Separator line if there are ranked + unranked */}
            {totalRanked > 0 && totalPool > 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '4px 12px', borderBottom: '1px dashed #1a1f2f' }}>
                  <span style={{ fontSize: '0.62rem', color: '#4b5563', fontStyle: 'italic' }}>
                    ── unranked ──
                  </span>
                </td>
              </tr>
            )}
            {/* Unranked pool players, sorted by OVR desc */}
            {poolPlayers.slice().sort((a, b) => b.overall - a.overall).map(p => (
              renderPlayerRow(p, -1, false)
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{ background: '#060a12', minHeight: '100vh', color: '#fff', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* HEADER */}
      <div style={{
        background: 'linear-gradient(180deg, #0d1117 0%, #080c14 100%)',
        borderBottom: `2px solid ${isLocked ? '#374151' : timerColor}`,
        padding: '14px 16px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, maxWidth: 900, margin: '0 auto' }}>
          <div>
            <div style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1.5 }}>Draft Priority Sheet</div>
            <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: 1 }}>
              <strong style={{ color: '#60a5fa' }}>{queue.length}</strong> ranked · <strong style={{ color: '#e2e8f0' }}>{Object.values(poolByPos).reduce((a, b) => a + b.length, 0)}</strong> available
            </div>
          </div>

          {/* Timer */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '1.8rem',
              fontWeight: 700,
              fontFamily: '"SF Mono", "Cascadia Code", "Fira Code", monospace',
              color: isLocked ? '#6b7280' : timerColor,
              lineHeight: 1,
              letterSpacing: 2,
            }}>
              {isLocked ? '🔒 LOCKED' : formatTime(timeRemaining)}
            </div>
            <div style={{ height: 3, width: 100, background: '#1e293b', borderRadius: 2, overflow: 'hidden', marginTop: 4, marginLeft: 'auto', marginRight: 'auto' }}>
              <div style={{ height: '100%', width: `${timerPct}%`, background: timerColor, transition: 'width 1s linear', borderRadius: 2 }} />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={saveSheet}
              disabled={saving || !dirty || isLocked}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: `1px solid ${dirty && !isLocked ? '#3b82f6' : '#1e293b'}`,
                background: dirty && !isLocked ? '#1e40af' : 'transparent',
                color: dirty && !isLocked ? '#fff' : '#374151',
                cursor: dirty && !isLocked ? 'pointer' : 'default',
                fontWeight: 700,
                fontSize: '0.8rem',
                transition: 'all 0.15s',
              }}
            >
              {saving ? 'Saving…' : '💾 Save Order'}
            </button>
            <button
              onClick={async () => {
                if (dirty) await saveSheet();
                onNavigate ? onNavigate(`/league/${leagueId}`) : onBack?.();
              }}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                background: '#22c55e',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            >
              Done
            </button>
          </div>
        </div>

        {isLocked && (
          <div style={{ maxWidth: 900, margin: '8px auto 0', fontSize: '0.72rem', color: '#6b7280', textAlign: 'center' }}>
            Draft locked — auto-draft will pick from your priority sheet, then best available by OVR
          </div>
        )}

        {!isLocked && queue.length > 0 && (
          <div style={{ maxWidth: 900, margin: '6px auto 0', fontSize: '0.7rem', color: '#4b5563', textAlign: 'center' }}>
            Click a rank number to type a new value — all other players cascade automatically
          </div>
        )}
      </div>

      {/* MAIN CONTENT */}
      <div style={{ maxWidth: 900, margin: '0 auto', width: '100%', padding: '8px 0' }}>
        {POSITION_GROUPS.map(renderPosGroup)}

        {Object.values(poolByPos).reduce((a, b) => a + b.length, 0) === 0 && queue.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: '#4b5563' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>🏟️</div>
            <div style={{ fontSize: '0.9rem' }}>Loading draft pool…</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Tiny inline stat bar for the compact attribute display
function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#4b5563', width: 24, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 3, background: '#121726', borderRadius: 2, overflow: 'hidden', minWidth: 50 }}>
        <div style={{
          width: `${Math.round((value / 99) * 100)}%`,
          height: '100%',
          background: statColor(value),
          borderRadius: 2,
        }} />
      </div>
      <span style={{ fontSize: '0.62rem', fontWeight: 600, color: statColor(value), width: 20, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
